import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../utils/api';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import {
  Loader2, MapPin, Building2, CheckCircle2, XCircle, ShieldCheck, Navigation,
  NavigationOff, Camera, RefreshCw, ChevronDown, ChevronRight, AlertTriangle,
} from 'lucide-react';

const todayISO = () => new Date().toISOString().slice(0, 10);

const FAILURE_CODES = [
  { v: 'checkin_too_early', label: 'Too early' },
  { v: 'already_checked_in', label: 'Already checked in' },
  { v: 'qr_required', label: 'QR required' },
  { v: 'selfie_required', label: 'Selfie required' },
  { v: 'shift_gps_consent_required', label: 'GPS consent (shift)' },
  { v: 'gps_consent_required', label: 'GPS consent (policy)' },
  { v: 'booking_not_confirmed', label: 'Not confirmed' },
  { v: 'gate_reject', label: 'Other block' },
];

// Format an attempt's UTC timestamp in the EVENT's local timezone.
const fmtLocal = (utcIso, tz) => {
  if (!utcIso) return '—';
  try {
    return new Date(utcIso).toLocaleString('en-US', {
      timeZone: tz || 'America/New_York',
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return new Date(utcIso).toLocaleString();
  }
};

const GpsChip = ({ ok }) => (
  <Badge variant="outline" className={ok ? 'text-emerald-700 border-emerald-200 bg-emerald-50' : 'text-slate-600 border-slate-200 bg-slate-50'} data-testid="attempt-gps-chip">
    {ok ? <Navigation className="w-3 h-3 mr-1" /> : <NavigationOff className="w-3 h-3 mr-1" />}
    {ok ? 'GPS ok' : 'No GPS'}
  </Badge>
);

const AttemptRow = ({ a, onOverride }) => {
  const blocked = a.result === 'blocked';
  const canOverride = blocked && a.booking_id && !a.override_applied && !a.clock_in_time;
  return (
    <div
      className={`flex items-start gap-3 py-2.5 px-3 rounded-lg border ${blocked ? 'border-red-200 bg-red-50/60' : 'border-slate-100 bg-white'}`}
      data-testid={`attempt-row-${a.id}`}
    >
      <div className="mt-0.5">
        {blocked
          ? <XCircle className="w-4 h-4 text-red-500" />
          : <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-slate-800">{fmtLocal(a.attempted_at, a.timezone)}</span>
          <Badge className={blocked ? 'bg-red-100 text-red-800 border-red-200' : 'bg-emerald-100 text-emerald-800 border-emerald-200'} data-testid="attempt-result-badge">
            {blocked ? 'Blocked' : 'Checked in'}
          </Badge>
          {a.http_status && <span className="text-[11px] text-slate-400">HTTP {a.http_status}</span>}
        </div>
        {blocked && a.failure_reason && (
          <div className="text-sm text-red-700 mt-0.5" data-testid="attempt-failure-reason">
            {a.failure_reason}
          </div>
        )}
        <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
          <GpsChip ok={a.gps_ok} />
          {a.onsite_fallback_used && (
            <Badge variant="outline" className="text-amber-700 border-amber-200 bg-amber-50" data-testid="attempt-fallback-chip">
              <MapPin className="w-3 h-3 mr-1" /> Onsite fallback used
            </Badge>
          )}
          {a.selfie_ok && (
            <Badge variant="outline" className="text-slate-600 border-slate-200"><Camera className="w-3 h-3 mr-1" /> Selfie</Badge>
          )}
          {a.override_applied && (
            <Badge variant="outline" className="text-cyan-700 border-cyan-200 bg-cyan-50" data-testid="attempt-override-chip">
              <ShieldCheck className="w-3 h-3 mr-1" /> Overridden by {a.override_by || 'admin'}
            </Badge>
          )}
        </div>
        {a.override_applied && a.override_reason && (
          <div className="text-[11px] text-cyan-800 mt-1">Reason: {a.override_reason}</div>
        )}
      </div>
      {canOverride && (
        <Button
          size="sm"
          className="bg-cyan-600 hover:bg-cyan-700 shrink-0"
          onClick={() => onOverride(a)}
          data-testid={`override-checkin-btn-${a.id}`}
        >
          <ShieldCheck className="w-4 h-4 mr-1" /> Override &amp; Check In
        </Button>
      )}
    </div>
  );
};

const AttemptGroup = ({ group }) => {
  const [open, setOpen] = useState(true);
  const head = group.attempts[0];
  const hasBlocked = group.attempts.some((a) => a.result === 'blocked' && !a.override_applied && !a.clock_in_time);
  return (
    <Card className={hasBlocked ? 'border-red-200' : ''} data-testid={`attempt-group-${group.key}`}>
      <button
        type="button"
        className="w-full flex items-center gap-3 p-3 text-left"
        onClick={() => setOpen((o) => !o)}
        data-testid={`attempt-group-toggle-${group.key}`}
      >
        {head.talent_photo
          ? <img src={head.talent_photo} alt={head.talent_name} className="w-9 h-9 rounded-full object-cover" />
          : <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center text-slate-500 font-bold">{head.talent_name?.[0] || '?'}</div>}
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-900 flex items-center gap-2">
            {head.talent_name}
            {hasBlocked && (
              <Badge className="bg-red-600 text-white border-red-600" data-testid="group-needs-attention">
                <AlertTriangle className="w-3 h-3 mr-1" /> Needs attention
              </Badge>
            )}
            <span className="text-xs font-normal text-slate-400">{group.attempts.length} attempt(s)</span>
          </div>
          <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
            <span>{head.shift_title}</span>
            {head.venue_name && <><MapPin className="w-3 h-3" />{head.venue_name}</>}
            {head.company_name && <><Building2 className="w-3 h-3" />{head.company_name}</>}
          </div>
        </div>
        {open ? <ChevronDown className="w-5 h-5 text-slate-300" /> : <ChevronRight className="w-5 h-5 text-slate-300" />}
      </button>
      {open && (
        <CardContent className="pt-0 space-y-2">
          {group.attempts.map((a) => (
            <AttemptRow key={a.id} a={a} onOverride={group.onOverride} />
          ))}
        </CardContent>
      )}
    </Card>
  );
};

const ClockInAttemptsPanel = () => {
  const [attempts, setAttempts] = useState([]);
  const [stats, setStats] = useState({ count: 0, blocked_count: 0, success_count: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [dateFrom, setDateFrom] = useState(todayISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const [talent, setTalent] = useState('');
  const [venue, setVenue] = useState('');
  const [result, setResult] = useState('all');
  const [gps, setGps] = useState('all');
  const [failureCode, setFailureCode] = useState('all');
  const [overridesOnly, setOverridesOnly] = useState(false);

  const [overrideTarget, setOverrideTarget] = useState(null);
  const [overrideReason, setOverrideReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isToday = dateFrom === todayISO() && dateTo === todayISO();

  const buildParams = useCallback(() => {
    const p = {};
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    if (talent.trim()) p.talent = talent.trim();
    if (venue.trim()) p.venue = venue.trim();
    if (result !== 'all') p.result = result;
    if (gps !== 'all') p.gps = gps;
    if (failureCode !== 'all') p.failure_code = failureCode;
    if (overridesOnly) p.overrides_only = true;
    return p;
  }, [dateFrom, dateTo, talent, venue, result, gps, failureCode, overridesOnly]);

  const fetchAttempts = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const r = await api.get('/attendance/clock-in-attempts', { params: buildParams() });
      setAttempts(r.data.attempts || []);
      setStats({ count: r.data.count, blocked_count: r.data.blocked_count, success_count: r.data.success_count });
    } catch (e) {
      toast.error('Failed to load clock-in attempts');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [buildParams]);

  useEffect(() => { fetchAttempts(); }, [fetchAttempts]);

  // Real-time polling while viewing TODAY.
  useEffect(() => {
    if (!isToday) return undefined;
    const t = setInterval(() => fetchAttempts(true), 20000);
    return () => clearInterval(t);
  }, [isToday, fetchAttempts]);

  const runOverride = async () => {
    if (!overrideTarget) return;
    if (!overrideReason.trim()) { toast.error('A reason is required'); return; }
    setSubmitting(true);
    try {
      await api.post('/attendance/override-checkin', null, {
        params: {
          booking_id: overrideTarget.booking_id,
          action: 'manual_checkin',
          reason: overrideReason.trim(),
          notes: `Original block: ${overrideTarget.failure_reason || overrideTarget.failure_code || 'n/a'}`,
        },
      });
      toast.success(`${overrideTarget.talent_name} checked in via admin override`);
      setOverrideTarget(null);
      setOverrideReason('');
      fetchAttempts(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Override failed');
    } finally {
      setSubmitting(false);
    }
  };

  const openOverride = (a) => {
    setOverrideTarget(a);
    setOverrideReason(`Onsite — technology prevented clock-in (${a.failure_reason || a.failure_code || 'blocked'}).`);
  };

  // Group by talent+shift; blocked-needing-attention groups first, newest first.
  const groups = useMemo(() => {
    const map = new Map();
    attempts.forEach((a) => {
      if (!map.has(a.group_key)) map.set(a.group_key, []);
      map.get(a.group_key).push(a);
    });
    const arr = [];
    map.forEach((list, key) => {
      // chronological within a group (oldest -> newest = the sequence)
      const sorted = [...list].sort((x, y) => new Date(x.attempted_at) - new Date(y.attempted_at));
      const needsAttention = sorted.some((a) => a.result === 'blocked' && !a.override_applied && !a.clock_in_time);
      const latest = Math.max(...sorted.map((a) => new Date(a.attempted_at).getTime()));
      arr.push({ key, attempts: sorted, needsAttention, latest, onOverride: openOverride });
    });
    arr.sort((a, b) => {
      if (a.needsAttention !== b.needsAttention) return a.needsAttention ? -1 : 1;
      return b.latest - a.latest;
    });
    return arr;
  }, [attempts]);

  return (
    <div className="space-y-4" data-testid="clock-in-attempts-panel">
      {/* Stat strip */}
      <div className="grid grid-cols-3 gap-3">
        <Card><CardContent className="p-3">
          <div className="text-2xl font-bold text-slate-900" data-testid="stat-total">{stats.count}</div>
          <div className="text-xs text-slate-500">Attempts</div>
        </CardContent></Card>
        <Card className="border-red-200"><CardContent className="p-3">
          <div className="text-2xl font-bold text-red-600" data-testid="stat-blocked">{stats.blocked_count}</div>
          <div className="text-xs text-slate-500">Blocked / failed</div>
        </CardContent></Card>
        <Card className="border-emerald-200"><CardContent className="p-3">
          <div className="text-2xl font-bold text-emerald-600" data-testid="stat-success">{stats.success_count}</div>
          <div className="text-xs text-slate-500">Successful</div>
        </CardContent></Card>
      </div>

      {/* Filters */}
      <Card><CardContent className="p-3 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs">From</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-40" data-testid="filter-date-from" />
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-40" data-testid="filter-date-to" />
          </div>
          <Button variant="outline" size="sm" className="h-9" onClick={() => { setDateFrom(todayISO()); setDateTo(todayISO()); }} data-testid="filter-today-btn">Today</Button>
          <div className="flex-1 min-w-[160px]">
            <Label className="text-xs">Talent</Label>
            <Input placeholder="Search name" value={talent} onChange={(e) => setTalent(e.target.value)} className="h-9" data-testid="filter-talent" />
          </div>
          <div className="flex-1 min-w-[160px]">
            <Label className="text-xs">Venue</Label>
            <Input placeholder="Search venue" value={venue} onChange={(e) => setVenue(e.target.value)} className="h-9" data-testid="filter-venue" />
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs">Result</Label>
            <Select value={result} onValueChange={setResult}>
              <SelectTrigger className="h-9 w-36" data-testid="filter-result"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All results</SelectItem>
                <SelectItem value="blocked">Blocked / failed</SelectItem>
                <SelectItem value="success">Successful</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">GPS</Label>
            <Select value={gps} onValueChange={setGps}>
              <SelectTrigger className="h-9 w-36" data-testid="filter-gps"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any GPS</SelectItem>
                <SelectItem value="ok">GPS ok</SelectItem>
                <SelectItem value="missing">No GPS</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Failure reason</Label>
            <Select value={failureCode} onValueChange={setFailureCode}>
              <SelectTrigger className="h-9 w-48" data-testid="filter-failure-code"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any reason</SelectItem>
                {FAILURE_CODES.map((f) => <SelectItem key={f.v} value={f.v}>{f.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 pb-1.5">
            <Switch checked={overridesOnly} onCheckedChange={setOverridesOnly} data-testid="filter-overrides-only" id="ov-only" />
            <Label htmlFor="ov-only" className="text-xs">Overrides only</Label>
          </div>
          <Button variant="outline" size="sm" className="h-9 ml-auto" onClick={() => fetchAttempts(true)} data-testid="refresh-attempts-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
        {isToday && (
          <div className="text-[11px] text-emerald-600 flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse inline-block" /> Live — auto-refreshing every 20s
          </div>
        )}
      </CardContent></Card>

      {/* List */}
      {loading ? (
        <div className="text-center py-12 text-slate-400"><Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading…</div>
      ) : groups.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-500">
          <ShieldCheck className="w-12 h-12 mx-auto mb-3 text-emerald-500" />
          <div className="font-medium">No clock-in attempts</div>
          <div className="text-xs">Nothing recorded for these filters.</div>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {groups.map((g) => <AttemptGroup key={g.key} group={g} />)}
        </div>
      )}

      {/* Override confirmation */}
      <AlertDialog open={!!overrideTarget} onOpenChange={(o) => { if (!o) setOverrideTarget(null); }}>
        <AlertDialogContent data-testid="override-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Override &amp; check in {overrideTarget?.talent_name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This manually checks the talent in for <strong>{overrideTarget?.shift_title}</strong>
              {overrideTarget?.venue_name ? ` at ${overrideTarget.venue_name}` : ''}. The original blocked
              attempt stays in the log. Your name, the time, and this reason are recorded for the audit trail.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1.5">
            <Label className="text-xs">Reason (required)</Label>
            <Input
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              placeholder="Why are you overriding?"
              data-testid="override-reason-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="override-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); runOverride(); }}
              disabled={submitting || !overrideReason.trim()}
              className="bg-cyan-600 hover:bg-cyan-700"
              data-testid="override-confirm-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ShieldCheck className="w-4 h-4 mr-1" />}
              Confirm check-in
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ClockInAttemptsPanel;
